import { bunkSleepCapacity } from "./planned-bunk-choices";

export type ClassicPreferences = {
  access?: "stairs" | "ladder" | "lower_bed" | "undecided";
  legroom?: "standard" | "longer" | "undecided";
  separateMattresses?: boolean;
};
export type ClassicRecommendation = {
  productLine: "basic";
  arrangement:
    | "single"
    | "parallel_pair"
    | "parallel_pair_center_stairs"
    | "perpendicular_lower_access"
    | "multiple";
  choices: number[];
  bunkCount: number;
  mattressCount: number;
  capacity: number;
  sharingRequired: boolean;
  title: string;
  explanation: string;
  designerReview: string;
  requiresDesignerApproval: true;
};

const longerEquivalent = [1, 1, 3, 3, 4, 4] as const;
const validChoice = (choice: number) =>
  Number.isInteger(choice) && choice >= 0 && choice < longerEquivalent.length;
export const MAX_CLASSIC_GUESTS = 100;

/** Advisory configuration only. Never changes the order, staircase charges, or
 * selected product line. Two parallel Classic units remain Classic, including
 * when central stairs are preferred. Room fit and both upper entries need review. */
export function recommendClassicConfiguration({
  choices,
  sleepers,
  preferences = {},
  exactSleeperCount = false,
}: {
  choices: number[];
  sleepers: number;
  preferences?: ClassicPreferences;
  exactSleeperCount?: boolean;
}): ClassicRecommendation | null {
  if (
    !Number.isInteger(sleepers) ||
    sleepers < 2 ||
    sleepers > MAX_CLASSIC_GUESTS ||
    !choices.length ||
    choices.length > MAX_CLASSIC_GUESTS ||
    !choices.every(validChoice)
  )
    return null;

  const proposed = choices.map((choice) =>
    preferences.legroom === "longer" ? longerEquivalent[choice] : choice,
  );
  // Preserve existing unit counts and sizes; propose extra units only as advice.
  // The legacy helper deduplicates preferences, so retain the instances here.
  let repeat = 0;
  const enough = () =>
    preferences.separateMattresses
      ? proposed.length * 2 >= sleepers
      : bunkSleepCapacity(proposed) >= sleepers;
  while (!enough()) proposed.push(proposed[repeat++ % choices.length]);
  const count = proposed.length;
  const stairs = preferences.access === "stairs";
  const easyLowerAccess = preferences.access === "lower_bed";
  const arrangement = easyLowerAccess
    ? "perpendicular_lower_access"
    : count === 1
      ? "single"
      : count === 2
        ? stairs
          ? "parallel_pair_center_stairs"
          : "parallel_pair"
        : "multiple";
  const title = easyLowerAccess
    ? "Classic with Easy Access Lower Bunks"
    : arrangement === "single"
      ? "A Classic bunk for your room"
      : arrangement === "parallel_pair_center_stairs"
        ? "Two Classics with stairs in the middle"
        : arrangement === "parallel_pair"
          ? "Two Classics, side by side"
          : `${count} Classic bunks${exactSleeperCount ? ` for ${sleepers} guests` : " for your room"}`;
  const arrangementCopy = easyLowerAccess
    ? "a perpendicular lower-bunk layout, with room to approach each lower bed from both sides"
    : count === 2
      ? `exploring two Classic bunk units side by side in a parallel arrangement${stairs ? ", with stairs in the middle" : ""}`
      : count === 1
        ? `exploring one Classic bunk${stairs ? " with stair access" : ""}`
        : `exploring ${count} Classic bunk units`;
  const goal =
    sleepers === 6 && !exactSleeperCount ? "at least six" : String(sleepers);
  const reasons = [
    `You’re making room for ${goal}${stairs ? " and prefer stairs" : ""}. We recommend ${arrangementCopy}.`,
  ];
  if (preferences.legroom === "longer")
    reasons.push(
      "We’ve considered longer mattresses for the extra legroom you requested.",
    );
  if (preferences.separateMattresses)
    reasons.push(
      "This starting point provides a separate mattress for each requested sleeper.",
    );
  if (easyLowerAccess)
    reasons.push(
      "Your designer will confirm the floor space, mattress sizes and clearances on both sides before approving this configuration.",
    );
  const sharingRequired = sleepers > count * 2;
  if (sharingRequired)
    reasons.push(
      "This capacity assumes two people sharing some full or queen mattresses.",
    );
  if (sleepers === 6 && !exactSleeperCount)
    reasons.push(
      "You selected 6+; we’ll confirm the exact guest count before finalizing the layout.",
    );
  return {
    productLine: "basic",
    arrangement,
    choices: proposed,
    bunkCount: count,
    mattressCount: count * 2,
    capacity: preferences.separateMattresses
      ? count * 2
      : bunkSleepCapacity(proposed),
    sharingRequired,
    title,
    explanation: reasons.join(" "),
    designerReview:
      "Your designer will confirm room dimensions, clearances, stair placement, and entry to each upper bunk. This is a layout recommendation; your selected bunks and priced extras remain unchanged.",
    requiresDesignerApproval: true,
  };
}
